import { useQuery, useStatus } from '@powersync/react';
import { useEffect, useMemo, useState } from 'react';

import { trashEntries, type TrashRow } from '@flow/domain';
import { queries, type WorkspaceSummary } from '@flow/sync';
import { ChoiceDialog, TrashList } from '@flow/ui/components';

import { formatDay } from '../capture/model.js';
import { useToday } from '../capture/use-today.js';
import { API_URL } from '../config.js';
import type { LocalDatabase } from '../db/powersync.js';
import { t } from '../i18n/es.js';
import { useTaskActions } from '../list/use-task-actions.jsx';
import { useSession } from '../session.jsx';
import { useSetViewHeader } from '../shell/AppLayout.jsx';

/**
 * The Trash (REC-05, design D2–D3 of f1-trash-search): what was deleted, read from the local
 * database (also offline), with «Restaurar» and «Eliminar definitivamente». Restoring is a local
 * command with «Deshacer»; deleting for good asks the server, which enqueues the purge, so it
 * needs a connection and says so.
 */

/** Rows the list holds; one more tells there are more (design D2). */
const LIMIT = 500;

export function TrashPage() {
  const { local, user } = useSession();
  const setHeader = useSetViewHeader();
  useEffect(() => {
    setHeader({ title: t.trash.title });
  }, [setHeader]);
  if (!local || !user) return null;
  return <Trash local={local} userId={user.id} />;
}

function Trash({ local, userId }: { local: LocalDatabase; userId: string }) {
  const today = useToday(userId);
  const workspaceQuery = queries.personalWorkspace();
  const { data: workspaces } = useQuery<WorkspaceSummary>(
    workspaceQuery.sql,
    workspaceQuery.params,
  );
  const workspaceId = workspaces[0]?.id ?? '';
  const trashQuery = workspaceId ? queries.trashedBlocks(workspaceId, LIMIT + 1) : queries.none();
  const { data: rows } = useQuery<TrashRow>(trashQuery.sql, trashQuery.params);
  const status = useStatus();
  const [confirm, setConfirm] = useState<{ id: string; title: string; descendants: number } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  // Days left count from the end of today (never more than there are); `today` moves at midnight.
  const now = `${today}T23:59:59.999Z`;
  const entries = useMemo(() => trashEntries(rows.slice(0, LIMIT), now), [rows, now]);

  const actions = useTaskActions({
    local,
    userId,
    workspaceId,
    stages: [],
    period: undefined,
    type: undefined,
    active: [],
    highlightsEnabled: false,
    today,
    blockOf: () => undefined,
    childrenOf: () => [],
  });

  const purge = async (id: string) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/v1/trash/purge`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [id] }),
      });
      if (!response.ok) throw new Error(`${response.status}`);
    } catch (cause) {
      console.error('[flow] no se ha podido eliminar para siempre', cause);
      setError(t.trash.purgeError);
    }
  };

  const dayOf = (instant: string) => {
    const day = new Date(instant).toLocaleDateString('en-CA');
    return day === today ? t.trash.today : formatDay(day, today);
  };

  return (
    <div className="page">
      {actions.notice || error ? (
        <p className="list-view__notice" role="status">
          {actions.notice ?? error}
        </p>
      ) : null}
      <TrashList
        entries={entries.map((entry) => ({
          id: entry.block.id,
          title: entry.block.title,
          kind: t.convert.kinds[entry.block.kind] ?? entry.block.kind,
          meta: t.trash.meta(
            entry.descendants.length,
            dayOf(entry.block.deleted_at),
            entry.daysLeft,
          ),
        }))}
        truncated={rows.length > LIMIT}
        onRestore={(id) => {
          const entry = entries.find((e) => e.block.id === id);
          if (entry) void actions.restore(id, entry.block.deleted_at);
        }}
        onPurge={(id) => {
          const entry = entries.find((e) => e.block.id === id);
          if (entry) {
            setConfirm({ id, title: entry.block.title, descendants: entry.descendants.length });
          }
        }}
        purgeDisabled={status.connected ? null : t.trash.offline}
        labels={t.trash}
      />
      {confirm ? (
        <ChoiceDialog
          title={t.trash.confirmTitle}
          message={t.trash.confirm(confirm.title, confirm.descendants)}
          choices={[{ id: 'purge', label: t.trash.confirmYes }]}
          onChoose={() => {
            const { id } = confirm;
            setConfirm(null);
            void purge(id);
          }}
          onCancel={() => setConfirm(null)}
          labels={{ cancel: t.trash.cancel }}
        />
      ) : null}
      {actions.dialogs}
    </div>
  );
}
